import { GppGoodOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import { DefinitionList, HelpPopover, ResourceState, designTokens } from '../../../design-system';
import { displayCertificateDate } from '../model/presentation';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type { CertificateVerificationPort } from '../port/CertificatesPort';

export function CertificateVerificationPage({
  token,
  port,
}: {
  token: string;
  port: CertificateVerificationPort;
}) {
  const resource = useAsyncResource(() => port.verifyCertificate(token), [port, token]);
  const item = resource.data;
  const valid = item?.status === 'VALID';
  const title = valid
    ? '유효한 증명서입니다'
    : item?.status === 'REVOKED'
      ? '폐기된 증명서입니다'
      : item?.status === 'EXPIRED'
        ? '검증 유효기간이 지났습니다'
        : '증명서를 확인할 수 없습니다';

  return <Box sx={{ minHeight: '100vh', bgcolor: designTokens.color.subtleSurface, display: 'grid', placeItems: 'center', p: 2 }}>
    <Paper variant="outlined" sx={{ width: '100%', maxWidth: 560, overflow: 'hidden', boxShadow: designTokens.shadow.raisedSurface }}>
      <Box sx={{ px: 3, py: 2.25, bgcolor: designTokens.color.inverseSurface, color: designTokens.color.inverseText }}><Typography variant="caption" sx={{ color: designTokens.color.inverseFaintText, fontWeight: 760, letterSpacing: '0.1em' }}>HEYDAY DOCUMENT VERIFICATION</Typography></Box>
      <ResourceState embedded loading={resource.loading} error={resource.error?.message} empty={false} onRetry={resource.reload}>
        <Stack spacing={2.5} sx={{ p: { xs: 2.5, sm: 4 }, alignItems: 'center' }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: valid ? designTokens.color.successSoft : designTokens.color.dangerSoft, color: valid ? designTokens.color.successDark : designTokens.color.danger }}>{valid ? <VerifiedOutlined sx={{ fontSize: 34 }} /> : <GppGoodOutlined sx={{ fontSize: 34 }} />}</Box>
          <Stack direction="row" sx={{ alignItems: 'center', textAlign: 'center' }}><Typography variant="h5" sx={{ fontWeight: 760 }}>{title}</Typography><HelpPopover title="증명서 진위확인">발급 시스템에 보관된 원본 기록을 기준으로 확인합니다. 개인정보는 최소한으로 표시하며, PDF 전체 내용은 발급받은 사람에게 확인하세요.</HelpPopover></Stack>
          {item && item.status !== 'NOT_FOUND' ? <Stack spacing={1.5} sx={{ width: '100%' }}>
            <DefinitionList items={[["증명서", item.displayName || '-'], ['성명', item.holderName || '-'], ['발급번호', item.issuanceNumber || '-'], ['발급일', displayCertificateDate(item.issuedAt)], ['검증 만료일', displayCertificateDate(item.expiresAt)]]} />
            <Box sx={{ px: 2, py: 1.5, bgcolor: designTokens.color.navigationSurface, borderRadius: designTokens.radius.surface }}>
              <Typography variant="caption" color="text.secondary">PDF SHA-256</Typography>
              <Typography variant="caption" component="p" sx={{ mt: 0.5, fontFamily: 'monospace', color: designTokens.color.primaryText, overflowWrap: 'anywhere' }}>{item.pdfSha256 || '-'}</Typography>
            </Box>
          </Stack> : <Alert severity="warning" sx={{ width: '100%' }}>QR 주소가 잘못되었거나 외부 진위확인을 제공하지 않는 발급본입니다.</Alert>}
        </Stack>
      </ResourceState>
    </Paper>
  </Box>;
}
